import { useObserver } from 'mobx-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { removeTypename } from 'shared/helpers/utils';

import {
  GqlDutchiePosLoyaltyConfig,
  GqlGetDutchiePosLoyaltyConfigQuery,
  GqlRewardsActivity,
  GqlRewardsListV2,
  useGetDutchiePosLoyaltyConfigQuery,
} from 'types/graphql';

import useUser from 'src/hooks/use-user';
import useUI from 'src/hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useGetRewards } from 'src/components/loyalty/use-get-rewards';
import { RedemptionType, TierEarningBasis } from 'src/components/loyalty/rewards/dpos-utils';

type UseLoyaltyReturn = {
  loyaltyConfig: GqlGetDutchiePosLoyaltyConfigQuery['getDutchiePosLoyaltyConfig'];
  loading: boolean;
  formatValueForDisplay: (value: number, label?: string, isLoyaltyPoints?: boolean) => string;
  formatValueForDaysForExpire: (daysToExpiration: number) => string;
  rewardsProgramName: string;
  balanceDaysToExpiration: number | null | undefined;
  badgeColor: string;
  currentTierName: string | undefined;
  currentTierThreshold: number | undefined;
  nextTierName: string | undefined;
  currentBalance: number;
  currentYearTierBalance: number;
  expirationDate: string | undefined;
  targetBalance: number;
  targetRewardsBalance: number;
  progressPercentageToNextTier: string;
  progressPercentageToNextReward: string;
  progressToNextReward: number;
  currentRewardsBalance: number;
  isLoyaltyMember: boolean;
  isPoints: boolean;
  hasTiers: boolean;
  redemptionType: string;
  isSingleTier: boolean;
  isDiscountRedemption: boolean;
  posCustomerId: string | undefined;
  filteredRewardsActivity?: Array<GqlRewardsActivity>;
  loyaltyEnterpriseConfigData?: GqlDutchiePosLoyaltyConfig | undefined;
  rewardsEnterpriseData?: GqlRewardsListV2 | undefined;
  showLoyaltyWidget?: boolean;
};

export const useLoyaltyV2 = (
  activeEnterpriseId: string | undefined = undefined,
  loyaltyEnterpriseConfigData: GqlDutchiePosLoyaltyConfig | undefined = undefined,
  rewardsEnterpriseData: GqlRewardsListV2 | undefined = undefined
): UseLoyaltyReturn => {
  const flags = useFlags();
  const isLoyaltyWidgetEnabled = flags['growth.ecomm.show-loyalty-widget.rollout'] ?? false;
  const isWidgetRedemptionTypeFixEnabled = flags['pos.ecomm.show-widget-redemption-type-fix.rollout'] ?? false;
  const isSkipSMSSendingEnabled = flags['pos.customers.skip-sms-sending.rollout'] ?? false;

  const { isKiosk } = useUI();
  const User = useUser();
  const isUserLoading = useObserver(() => User.loading);
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const birthday = useObserver(() => User.birthday);
  const phone = useObserver(() => User.phone);
  const isUserLoadedAndLoggedIn = !isUserLoading && isLoggedIn;

  const { dispensary } = useDispensary();
  const isDutchiePOSLoyaltyEnabled = dispensary?.consumerDispensaryIntegrations?.dutchieposloyalty?.enabled;

  const skip = !isLoyaltyWidgetEnabled || !isUserLoadedAndLoggedIn || !isDutchiePOSLoyaltyEnabled || isKiosk;

  const { data: loyaltyConfigData, loading: loyaltyConfigLoading } = useGetDutchiePosLoyaltyConfigQuery({
    variables: {
      dispensaryId: dispensary?.id,
    },
    skip: !!loyaltyEnterpriseConfigData || skip,
  });

  const { data: rewardsData, loading: rewardsLoading } = useGetRewards({
    dispensaryId: dispensary?.id,
    birthday,
    phone,
    pin: isSkipSMSSendingEnabled ? 'skip' : '',
    skip: !!rewardsEnterpriseData || skip,
  });

  const loyaltyConfig = activeEnterpriseId
    ? loyaltyEnterpriseConfigData
    : loyaltyConfigData?.getDutchiePosLoyaltyConfig;
  const rewards = activeEnterpriseId ? rewardsEnterpriseData : rewardsData?.getRewardsV2;
  const rewardsActivity = rewards?.rewardsActivity;
  const isLoyaltyMember = rewards?.isLoyaltyMember ?? false;
  const isPoints = loyaltyConfig?.tierEarningBasis === TierEarningBasis.points;
  const isDiscountRedemption =
    isWidgetRedemptionTypeFixEnabled && loyaltyConfig?.redemptionType?.toLowerCase() === RedemptionType.discount;
  const filteredRewardsActivity = rewardsActivity
    ?.filter((rewardActivity) => rewardActivity !== null)
    .map((rewardData) => removeTypename(rewardData));

  // Tiers calculations for single tier or multiple tiers
  const currentTierIndex = loyaltyConfig?.tiers?.findIndex((tier) => tier?.id === rewards?.tierId);
  const hasMatchingTier = currentTierIndex !== undefined && currentTierIndex !== -1;
  const currentTier = hasMatchingTier ? loyaltyConfig?.tiers?.[currentTierIndex] : undefined;
  const nextTier = hasMatchingTier ? loyaltyConfig?.tiers?.[currentTierIndex + 1] : undefined;
  const { onlineName } = loyaltyConfig ?? {};
  const { badgeColor, threshold: currentTierThreshold = 0, name: currentTierName } = currentTier ?? {};
  const { name: nextTierName, threshold: nextTierThreshold } = nextTier ?? {};

  const currentBalance = Number(rewards?.balance ?? 0);
  const currentYearTierBalance = Number(rewards?.currentYearTierBalance ?? 0);
  const expirationDate = rewards?.expirationDate;

  // Handle single tier vs multiple tiers logic
  const isSingleTier = loyaltyConfig?.tiers?.length === 1;
  const targetBalance = isSingleTier ? currentTierThreshold : nextTierThreshold ?? 0;
  const progressToNextTier = isSingleTier ? currentTierThreshold : targetBalance - currentTierThreshold;
  const currentProgress = isSingleTier ? currentYearTierBalance : currentYearTierBalance - currentTierThreshold;
  const progressPercentageToNextTier =
    progressToNextTier === 0 ? '0%' : `${Math.min((currentProgress / progressToNextTier) * 100, 100)}%`;

  // Rewards calculations
  const rewardslist =
    rewards?.rewards.filter((reward) => reward?.available).sort((a, b) => (a?.costV2 ?? 0) - (b?.costV2 ?? 0)) ?? [];
  const nextReward = rewardslist.find((reward) => currentBalance < (reward?.costV2 ?? 0));
  const targetRewardsBalance = nextReward?.value ? Number(nextReward.costV2) : 0;
  const progressToNextReward = targetRewardsBalance - currentBalance;
  const progressPercentageToNextReward =
    progressToNextReward === 0 ? '0%' : `${(currentProgress / progressToNextReward) * 100}%`;

  const balanceDaysToExpiration = expirationDate
    ? Math.ceil((new Date(expirationDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const formatValueForDisplay = (value: number, label: string, isLoyaltyPoints: boolean): string => {
    if (isLoyaltyPoints) {
      return `${String(parseFloat(value.toFixed(2)))} ${label}`;
    }

    return currencyFormatter.format(value);
  };

  const formatValueForDaysForExpire = (daysToExpiration: number): string => {
    const hasTiers = (loyaltyConfig?.tiers?.length ?? 0) > 0;
    let label;

    if (daysToExpiration > 0) {
      label = `Expire in ${daysToExpiration} days`;
    } else if (hasTiers) {
      label = `${isDiscountRedemption ? 'Rewards points' : 'Rewards dollars'} expire today`;
    } else {
      label = isDiscountRedemption ? 'Rewards points expire today' : 'Rewards expire today';
    }

    return label;
  };

  const showLoyaltyWidget =
    loyaltyConfig?.isEnabled &&
    ((isLoyaltyMember && loyaltyConfig.loyaltyRequireOptIn) || !loyaltyConfig.loyaltyRequireOptIn);

  return {
    loyaltyConfig,
    loading: loyaltyConfigLoading || rewardsLoading,
    formatValueForDisplay,
    rewardsProgramName: onlineName ?? 'Rewards',
    balanceDaysToExpiration: balanceDaysToExpiration && balanceDaysToExpiration < 0 ? 0 : balanceDaysToExpiration,
    badgeColor: badgeColor ?? '#ffffff',
    currentTierName,
    currentTierThreshold,
    nextTierName,
    currentBalance,
    currentYearTierBalance,
    targetBalance,
    progressPercentageToNextTier,
    progressPercentageToNextReward,
    progressToNextReward,
    targetRewardsBalance,
    currentRewardsBalance: currentBalance,
    isLoyaltyMember,
    isPoints,
    redemptionType: loyaltyConfig?.redemptionType ?? '',
    hasTiers: (loyaltyConfig?.tiers?.length ?? 0) > 0,
    isSingleTier,
    isDiscountRedemption,
    posCustomerId: rewards?.posCustomerId ?? '',
    filteredRewardsActivity,
    formatValueForDaysForExpire,
    expirationDate: expirationDate ?? '',
    showLoyaltyWidget,
  };
};
